// Jupiter's four large moons: Meeus, Astronomical Algorithms (2nd ed.),
// chapter 44, the lower-accuracy method (main periodic terms of the Sampson
// and Lieske theory), giving each moon's angle u from inferior conjunction
// and its distance r in Jupiter radii, at the instant its light left. They
// are placed in three dimensions about Jupiter's IAU pole (Archinal et al.
// 2011), so the view shows them in front of the disc, behind it or in its
// shadow. Checked against JPL Horizons in tests/astro-coords.test.js.
import { DEG } from './coords.js';

export const GALILEAN = [
  { name: 'Io', mag: -1.68 },
  { name: 'Europa', mag: -1.41 },
  { name: 'Ganymede', mag: -2.09 },
  { name: 'Callisto', mag: -1.05 },
];
// The moons' magnitudes at 1 AU from the Sun and the observer, V(1,0)
// (Allen's Astrophysical Quantities, 4th ed., table 12.8).

const sin = (d) => Math.sin(d * DEG);
const cos = (d) => Math.cos(d * DEG);

// u (degrees) and r (Jupiter radii) for the four moons at jdTT.
export function galileanOrbits(jdTT, out = [{ u: 0, r: 0 }, { u: 0, r: 0 }, { u: 0, r: 0 }, { u: 0, r: 0 }]) {
  const d = jdTT - 2451545.0;
  const V = 172.74 + 0.00111588 * d;
  const M = 357.529 + 0.9856003 * d;
  const N = 20.020 + 0.0830853 * d + 0.329 * sin(V);
  const J = 66.115 + 0.9025179 * d - 0.329 * sin(V);
  const A = 1.915 * sin(M) + 0.020 * sin(2 * M);
  const B = 5.555 * sin(N) + 0.168 * sin(2 * N);
  const K = J + A - B;
  const R = 1.00014 - 0.01671 * cos(M) - 0.00014 * cos(2 * M);
  const r = 5.20872 - 0.25208 * cos(N) - 0.00611 * cos(2 * N);
  const delta = Math.sqrt(r * r + R * R - 2 * r * R * cos(K));
  const psi = Math.asin((R / delta) * sin(K)) / DEG;
  const t = d - delta / 173;
  let u1 = 163.8069 + 203.4058646 * t + psi - B;
  let u2 = 358.4140 + 101.2916335 * t + psi - B;
  let u3 = 5.7176 + 50.2345180 * t + psi - B;
  let u4 = 224.8092 + 21.4879800 * t + psi - B;
  const G = 331.18 + 50.310482 * t;
  const H = 87.45 + 21.569231 * t;
  const c1 = 0.473 * sin(2 * (u1 - u2));
  const c2 = 1.065 * sin(2 * (u2 - u3));
  const c3 = 0.165 * sin(G);
  const c4 = 0.843 * sin(H);
  out[0].r = 5.9057 - 0.0244 * cos(2 * (u1 - u2));
  out[1].r = 9.3966 - 0.0882 * cos(2 * (u2 - u3));
  out[2].r = 14.9883 - 0.0216 * cos(G);
  out[3].r = 26.3627 - 0.1939 * cos(H);
  u1 += c1; u2 += c2; u3 += c3; u4 += c4;
  out[0].u = u1; out[1].u = u2; out[2].u = u3; out[3].u = u4;
  return out;
}

// Jupiter's north pole, J2000 equator (IAU, the secular terms).
export function jupiterPole(jdTT, out = [0, 0, 0]) {
  const T = (jdTT - 2451545.0) / 36525;
  const a = (268.056595 - 0.006499 * T) * DEG, dd = (64.495303 + 0.002413 * T) * DEG;
  out[0] = Math.cos(dd) * Math.cos(a); out[1] = Math.cos(dd) * Math.sin(a); out[2] = Math.sin(dd);
  return out;
}

// Each moon's offset from Jupiter's centre in Jupiter radii, J2000 equator.
// toEarth: unit vector from Jupiter to the observer (J2000); pole: Jupiter's.
// In Jupiter's equatorial plane, e points at the observer's projection and
// w is the sky's west there; a moon at angle u lies at r (cos u e + sin u w).
export function galileanOffsets(jdTT, toEarth, pole, out) {
  const orb = galileanOrbits(jdTT);
  const pe = toEarth[0] * pole[0] + toEarth[1] * pole[1] + toEarth[2] * pole[2];
  let ex = toEarth[0] - pe * pole[0], ey = toEarth[1] - pe * pole[1], ez = toEarth[2] - pe * pole[2];
  const el = Math.hypot(ex, ey, ez); ex /= el; ey /= el; ez /= el;
  // w = pole x e, then turned to the sky's west (decreasing RA): the
  // direction from the observer to Jupiter is t = -toEarth, and east on the
  // sky there is z x t.
  let wx = pole[1] * ez - pole[2] * ey, wy = pole[2] * ex - pole[0] * ez, wz = pole[0] * ey - pole[1] * ex;
  const tx = -toEarth[0], ty = -toEarth[1];
  const eastX = -ty, eastY = tx; // z x t (its z part is 0)
  if (wx * eastX + wy * eastY > 0) { wx = -wx; wy = -wy; wz = -wz; }
  for (let i = 0; i < 4; i++) {
    const u = orb[i].u * DEG, r = orb[i].r;
    const o = out[i];
    o[0] = r * (Math.cos(u) * ex + Math.sin(u) * wx);
    o[1] = r * (Math.cos(u) * ey + Math.sin(u) * wy);
    o[2] = r * (Math.cos(u) * ez + Math.sin(u) * wz);
  }
  return out;
}
